// src/components/Admin/SellerManagement/CoinResetAssuranceSection.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toBlob } from 'html-to-image';
import { AlertTriangle, CheckCircle2, Image as ImageIcon, Info, Loader2, XCircle } from 'lucide-react';

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import { useToast } from "../../../hooks/use-toast";
import { getCoinResetAssurance, grantCoinResetAssurance } from '../../../services/AdminApis';

const formatDate = (value) => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(value));
};

// passed === null means "not applicable" (no Welcome Set) or "not implemented yet" (KYC).
const CriterionIcon = ({ passed, className = "" }) => {
    if (passed === true) return <CheckCircle2 className={`h-4 w-4 shrink-0 text-green-600 ${className}`} />;
    if (passed === false) return <XCircle className={`h-4 w-4 shrink-0 text-destructive ${className}`} />;
    return <Info className={`h-4 w-4 shrink-0 text-muted-foreground ${className}`} />;
};

const StatusBadge = ({ alreadyClaimed, hasWelcomeSet, isEligible }) => {
    if (alreadyClaimed) return <Badge variant="secondary">Already claimed</Badge>;
    if (!hasWelcomeSet) return <Badge variant="outline">No Welcome Set</Badge>;
    if (isEligible) return <Badge className="bg-green-600 text-white border-transparent hover:bg-green-600/80">Eligible</Badge>;
    return <Badge variant="destructive">Not eligible</Badge>;
};

const Stat = ({ label, value }) => (
    <div>
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="font-semibold">{value}</div>
    </div>
);

// Criteria read top-to-bottom within each column, two columns on wider screens.
const CriteriaGrid = ({ criteria }) => (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        {criteria.map((c) => (
            <li key={c.key} className="flex items-start gap-2 text-sm leading-snug">
                <CriterionIcon passed={c.passed} className="mt-0.5" />
                <div className="min-w-0">
                    <span className={c.passed === false && c.blocking ? 'font-medium text-destructive' : 'font-medium'}>
                        {c.label}
                    </span>
                    {!c.blocking && <span className="ml-1.5 text-xs text-muted-foreground">(advisory)</span>}
                    <div className="text-muted-foreground text-xs">{c.detail}</div>
                </div>
            </li>
        ))}
    </ul>
);

// Criteria that are internal admin cues, not something the seller can act on — kept
// out of the shared image along with the duplicate-account warnings.
const SHARE_CARD_HIDDEN_CRITERIA = new Set([
    'account_in_good_standing',
    'kyc_verified',
    'not_previously_claimed',
]);

/**
 * Off-screen node captured by "Copy as Image". Deliberately omits the duplicate-account
 * warnings — those are an internal abuse signal and must not end up in a shared screenshot.
 */
const CRAShareCard = ({ data, sellerProfileId }) => {
    const { criteria = [], welcome_set: welcomeSet, coins_to_return: coinsToReturn,
        returned_coin_expiry_days: expiryDays, claim_window_opens_at: opensAt,
        claim_window_closes_at: closesAt, is_eligible: isEligible, existing_claim: existingClaim } = data;

    return (
        <div className="w-full overflow-hidden rounded-xl border bg-white shadow-sm">
            <div className="flex items-center justify-between gap-4 border-b bg-slate-50 px-5 py-3">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Welcome Set
                    </p>
                    <h3 className="text-base font-bold leading-snug">Coin Reset Assurance — Seller #{sellerProfileId}</h3>
                </div>
                <StatusBadge
                    alreadyClaimed={Boolean(existingClaim)}
                    hasWelcomeSet={Boolean(welcomeSet)}
                    isEligible={isEligible}
                />
            </div>

            <div className="space-y-4 p-5">
                {welcomeSet && (
                    <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 p-3 text-sm">
                        <Stat label="Coins to return" value={coinsToReturn} />
                        <Stat label="Valid for" value={`${expiryDays} days from credit`} />
                        <Stat label="Welcome Set purchased" value={formatDate(welcomeSet.purchased_at)} />
                        <Stat label="Coins ran out" value={formatDate(welcomeSet.depleted_at)} />
                        <Stat label="Claim window opens" value={formatDate(opensAt)} />
                        <Stat label="Claim window closes" value={formatDate(closesAt)} />
                    </div>
                )}
                <CriteriaGrid criteria={criteria.filter((c) => !SHARE_CARD_HIDDEN_CRITERIA.has(c.key))} />
            </div>
        </div>
    );
};

const CoinResetAssuranceSection = ({ sellerProfileId, onGranted }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [confirmOverride, setConfirmOverride] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const shareCardRef = useRef(null);
    const { toast } = useToast();

    const fetchEligibility = useCallback(async () => {
        try {
            const result = await getCoinResetAssurance(sellerProfileId);
            setData(result);
            setError('');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load Coin Reset Assurance eligibility.');
        } finally {
            setIsLoading(false);
        }
    }, [sellerProfileId]);

    useEffect(() => {
        setIsLoading(true);
        fetchEligibility();
    }, [fetchEligibility]);

    const openDialog = () => {
        setReason('');
        setConfirmOverride(false);
        setIsDialogOpen(true);
    };

    const handleGrant = async () => {
        if (!reason.trim()) {
            toast({ variant: "destructive", title: "Reason required", description: "Please record why these coins are being returned." });
            return;
        }
        setIsSubmitting(true);
        try {
            const result = await grantCoinResetAssurance(sellerProfileId, {
                reason: reason.trim(),
                // Only flag an override when the seller actually fails a blocking criterion —
                // the backend rejects a needless override from a non-senior admin.
                is_override: Boolean(data && !data.is_eligible && !data.existing_claim),
            });
            toast({
                title: "Coins returned",
                description: `${result.coins_returned} coins credited, expiring ${formatDate(result.expires_at)}.`,
            });
            setIsDialogOpen(false);
            await fetchEligibility();
            if (onGranted) onGranted();
        } catch (err) {
            toast({
                variant: "destructive",
                title: "Grant failed",
                description: err.response?.data?.detail || "An unexpected error occurred.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const copyCardAsImage = async () => {
        if (!shareCardRef.current) return;

        if (!navigator.clipboard?.write || !window.ClipboardItem) {
            toast({
                variant: "destructive",
                title: "Copy Not Supported",
                description: "This browser does not support copying images to the clipboard.",
            });
            return;
        }

        try {
            setIsCopying(true);
            const captureNode = shareCardRef.current;

            const imageBlob = await toBlob(captureNode, {
                backgroundColor: '#ffffff',
                width: Math.ceil(captureNode.scrollWidth),
                height: Math.ceil(captureNode.scrollHeight) + 20,
                pixelRatio: 2,
                cacheBust: true,
                style: { position: 'static', left: '0', top: '0', overflow: 'visible' },
            });

            if (!imageBlob) throw new Error('Could not create an image of the CRA summary.');

            await navigator.clipboard.write([new ClipboardItem({ 'image/png': imageBlob })]);
            toast({
                title: "Copied as Image",
                description: "The CRA summary is ready to paste from your clipboard.",
            });
        } catch (copyError) {
            console.error('Could not copy the CRA summary as an image:', copyError);
            toast({
                variant: "destructive",
                title: "Copy Failed",
                description: "Could not copy the CRA summary as an image.",
            });
        } finally {
            setIsCopying(false);
        }
    };

    if (isLoading) {
        return (
            <Card className="mt-4">
                <CardHeader><CardTitle>Coin Reset Assurance (CRA)</CardTitle></CardHeader>
                <CardContent className="text-sm text-muted-foreground">Checking eligibility…</CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="mt-4">
                <CardHeader><CardTitle>Coin Reset Assurance (CRA)</CardTitle></CardHeader>
                <CardContent className="text-sm text-destructive">{error}</CardContent>
            </Card>
        );
    }

    if (!data) return null;

    const {
        is_eligible: isEligible,
        criteria = [],
        warnings = [],
        welcome_set: welcomeSet,
        coins_to_return: coinsToReturn,
        returned_coin_expiry_days: expiryDays,
        claim_window_opens_at: windowOpensAt,
        claim_window_closes_at: windowClosesAt,
        existing_claim: existingClaim,
        can_override: canOverride,
    } = data;

    const failedCriteria = criteria.filter((c) => c.blocking && c.passed === false);
    const alreadyClaimed = Boolean(existingClaim);
    const needsOverride = !isEligible && !alreadyClaimed;
    const canGrant = !alreadyClaimed && coinsToReturn > 0 && (isEligible || canOverride);

    return (
        <Card className="mt-4">
            {/* Off-screen node captured by "Copy as Image" */}
            <div
                ref={shareCardRef}
                aria-hidden="true"
                className="fixed top-0 bg-white p-2"
                style={{ left: '-10000px', width: '720px', pointerEvents: 'none' }}
            >
                <CRAShareCard data={data} sellerProfileId={sellerProfileId} />
            </div>

            <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <CardTitle>Coin Reset Assurance (CRA)</CardTitle>
                        <StatusBadge alreadyClaimed={alreadyClaimed} hasWelcomeSet={Boolean(welcomeSet)} isEligible={isEligible} />
                    </div>
                    <Button variant="outline" size="sm" onClick={copyCardAsImage} disabled={isCopying}>
                        {isCopying
                            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            : <ImageIcon className="mr-2 h-4 w-4" />}
                        Copy as Image
                    </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                    Returns 100% of the Welcome Set coins, valid for {expiryDays} days from the credit date.
                    Claimable once per seller.
                </p>
            </CardHeader>

            <CardContent className="space-y-3">
                {welcomeSet && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm p-3 rounded-md bg-muted/50">
                        <Stat label="Coins to return" value={coinsToReturn} />
                        <Stat label="Welcome Set purchased" value={formatDate(welcomeSet.purchased_at)} />
                        <Stat label="Coins ran out" value={formatDate(welcomeSet.depleted_at)} />
                        <Stat label="Valid for" value={`${expiryDays} days from credit`} />
                        <Stat label="Claim window opens" value={formatDate(windowOpensAt)} />
                        <Stat label="Claim window closes" value={formatDate(windowClosesAt)} />
                    </div>
                )}

                {alreadyClaimed && (
                    <div className="text-sm p-3 rounded-md border bg-muted/30">
                        Claimed on <span className="font-semibold">{formatDate(existingClaim.claimed_at)}</span> —{' '}
                        {existingClaim.coins_returned} coins returned
                        {existingClaim.is_override ? ' (eligibility overridden)' : ''}.
                        {existingClaim.reason ? <span className="block text-muted-foreground mt-1">“{existingClaim.reason}”</span> : null}
                    </div>
                )}

                {warnings.length > 0 && (
                    <div className="text-sm p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900">
                        <div className="flex items-center gap-2 font-semibold">
                            <AlertTriangle className="h-4 w-4" />
                            Possible duplicate accounts (internal — not included in the shared image)
                        </div>
                        <ul className="mt-1 list-disc pl-5">
                            {warnings.map((w) => (
                                <li key={w.seller_profile_id}>
                                    Seller #{w.seller_profile_id} (same {w.matched_on}) claimed {w.coins_returned} coins
                                    on {formatDate(w.claimed_at)}.
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <CriteriaGrid criteria={criteria} />

                <div className="flex flex-wrap items-center gap-3 pt-1">
                    <Button onClick={openDialog} disabled={!canGrant}>
                        {needsOverride ? 'Override & Return Coins' : 'Return Welcome Set Coins'}
                        {coinsToReturn > 0 ? ` (${coinsToReturn})` : ''}
                    </Button>
                    {!canGrant && (
                        <span className="text-sm text-muted-foreground">
                            {alreadyClaimed
                                ? 'CRA has already been claimed for this seller.'
                                : coinsToReturn > 0
                                    ? 'Seller does not meet the CRA criteria. Only CX Admin, CX TL or Super Admin can override.'
                                    : 'There are no Welcome Set coins to return.'}
                        </span>
                    )}
                </div>
            </CardContent>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Return {coinsToReturn} Welcome Set coins?</DialogTitle>
                        <DialogDescription>
                            The coins are credited immediately and expire in {expiryDays} days. This can only be done
                            once per seller and is recorded against your admin account.
                        </DialogDescription>
                    </DialogHeader>

                    {needsOverride && (
                        <div className="text-sm p-3 rounded-md border border-destructive/40 bg-destructive/5">
                            <div className="flex items-center gap-2 font-semibold text-destructive">
                                <AlertTriangle className="h-4 w-4" />
                                This seller does not meet the CRA criteria
                            </div>
                            <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                                {failedCriteria.map((c) => (
                                    <li key={c.key}>{c.label} — {c.detail}</li>
                                ))}
                            </ul>
                            <div className="flex items-start gap-2 mt-3">
                                <Checkbox
                                    id="cra-override"
                                    checked={confirmOverride}
                                    onCheckedChange={(checked) => setConfirmOverride(checked === true)}
                                />
                                <Label htmlFor="cra-override" className="text-sm font-normal leading-snug">
                                    Overide CRA eligibility.
                                </Label>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="cra-reason">Reason</Label>
                        <Textarea
                            id="cra-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Seller raised ticket #1234; Welcome Set leads did not convert."
                            maxLength={500}
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleGrant}
                            disabled={isSubmitting || !reason.trim() || (needsOverride && !confirmOverride)}
                        >
                            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Confirm return
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default CoinResetAssuranceSection;
